import VideoPlayer from "@/components/video/VideoPlayer";
import VideoVerificationPanel from "@/components/video/VideoVerificationPanel";
import { useVideoVerification } from "@/hooks/useVideoVerification";
import { VIDEO_CHANNELS, getVideoChannel } from "@shared/videoChannels";
import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useRoute, useSearchParams } from "wouter";

export default function VideoPopout() {
  const [, params] = useRoute<{ channelSlug: string }>("/video/popout/:channelSlug");
  const [, setLocation] = useLocation();
  const [searchParams] = useSearchParams();
  const asOf = searchParams.has("asOf") ? searchParams.get("asOf") : null;
  const channel = params?.channelSlug ? getVideoChannel(params.channelSlug) : undefined;
  const channelId = channel ? `youtube:${channel.youtubeChannelId}` as const : null;
  const verification = useVideoVerification(asOf, channelId);
  const historicalRecord = channelId ? verification.view?.channels.find((record) => record.channelId === channelId) : undefined;
  const canonical = channel ? `https://tyl.ee/video/${channel.slug}` : "https://tyl.ee/video";

  return (
    <main className="video-popout-shell">
      <Helmet>
        <title>{channel ? asOf === null ? `${channel.name} Live Video Pop-out | Tylee Smart Markets` : `${historicalRecord?.channel.name ?? "Historical channel"} Playback Verification | Tylee Smart Markets` : "Video channel not found | Tylee Smart Markets"}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={canonical} />
      </Helmet>
      {channel ? (
        <>
          {asOf === null ? <VideoPlayer channel={channel} compact /> : (
            <section className="video-historical-actions" aria-label="Historical video notice">
              <p className="home-kicker">HISTORICAL VERIFICATION VIEW</p>
              <h1>Live video is hidden for a historical boundary.</h1>
            </section>
          )}
          {channelId && <VideoVerificationPanel
            asOf={asOf}
            channelId={channelId}
            status={verification.status}
            view={verification.view}
            onApplyBoundary={(boundary) => setLocation(`/video/popout/${channel.slug}${boundary ? `?asOf=${encodeURIComponent(boundary)}` : ""}`)}
          />}
          <p className="video-popout-navigation-label">Current channel navigation · names and category labels come from the current roster.</p>
          <nav className="video-popout-switcher" aria-label="Current channel navigation">
            {VIDEO_CHANNELS.map((candidate) => (
              <button
                key={candidate.slug}
                type="button"
                aria-pressed={candidate.slug === channel.slug}
                onClick={() => setLocation(`/video/popout/${candidate.slug}${asOf !== null ? `?asOf=${encodeURIComponent(asOf)}` : ""}`)}
              >
                {candidate.name}
              </button>
            ))}
          </nav>
        </>
      ) : (
        <section className="video-not-found">
          <h1>Channel not found</h1>
          <p>This pop-out URL is not part of the verified permanent roster.</p>
          <a href="/video">Open the Video newsroom</a>
        </section>
      )}
    </main>
  );
}
